import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";

function HoverZoomSplit({ src, zoomSrc, width, height, zoomScale = 2 }) {
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [showZoom, setShowZoom] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const imgRef = useRef(null);

  const rawSrc = zoomSrc || src;
  const safeSrc = rawSrc.replace(
    /[()]/g,
    (c) => "%" + c.charCodeAt(0).toString(16)
  );

  // measure image size after load
  useEffect(() => {
    if (imgRef.current) {
      setImgSize({
        w: imgRef.current.offsetWidth,
        h: imgRef.current.offsetHeight,
      });
    }
  }, [src, width, height]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    const lensW = imgSize.w * 0.25; // lens = 25% of image
    const lensH = imgSize.h * 0.25;

    let x = e.clientX - rect.left - lensW / 2;
    let y = e.clientY - rect.top - lensH / 2;

    if (x < 0) x = 0;
    if (y < 0) y = 0;
    if (x > rect.width - lensW) x = rect.width - lensW;
    if (y > rect.height - lensH) y = rect.height - lensH;

    setLensPos({ x, y, lensW, lensH });
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-block",
        width: width || "100%",
      }}
    >
      {/* Original image */}
      <Box
        sx={{
          position: "relative",
          width: width ? `${width}px` : "100%",
          height: height ? `${height}px` : "auto",
          border: "1px solid #ddd",
          overflow: "hidden",
          cursor: "crosshair",
        }}
        onMouseEnter={() => setShowZoom(true)}
        onMouseLeave={() => setShowZoom(false)}
        onMouseMove={handleMouseMove}
      >
        <img
          ref={imgRef}
          src={src}
          alt="Zoomable"
          style={{
            width: width ? `${width}px` : "100%",
            height: height ? `${height}px` : "auto",
            objectFit: "cover",
            display: "block",
          }}
          onLoad={() => {
            if (imgRef.current) {
              setImgSize({
                w: imgRef.current.offsetWidth,
                h: imgRef.current.offsetHeight,
              });
            }
          }}
        />

        {/* Lens */}
        {showZoom && imgSize.w > 0 && imgSize.h > 0 && (
          <Box
            sx={{
              position: "absolute",
              left: lensPos.x,
              top: lensPos.y,
              width: lensPos.lensW,
              height: lensPos.lensH,
              backgroundColor: "rgba(255,255,255,0.3)",
              border: "1px solid #aaa",
              pointerEvents: "none",
            }}
          />
        )}

        {/* Hover text */}
        {!showZoom && (
          <Box
            sx={{
              position: "absolute",
              bottom: "10px",
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "rgba(0,0,0,0.7)",
              px: 2,
              py: 0.5,
              borderRadius: "10px",
            }}
          >
            <Typography variant="body2" sx={{ color: "#fff" }}>
              + Hover to Zoom
            </Typography>
          </Box>
        )}
      </Box>

      {/* Zoom panel */}
      {showZoom && imgSize.w > 0 && imgSize.h > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: (width || imgSize.w) + 16, // place right of image
            width: width ? `${width}px` : `${imgSize.w}px`,
            height: height ? `${height}px` : `${imgSize.h}px`,
            border: "1px solid #ddd",
            zIndex: 9999,
            backgroundImage: `url("${safeSrc}")`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${imgSize.w * zoomScale}px ${
              imgSize.h * zoomScale
            }px`,
            backgroundPosition: `-${lensPos.x * zoomScale}px -${
              lensPos.y * zoomScale
            }px`,
          }}
        />
      )}
    </Box>
  );
}

export default HoverZoomSplit;
